import * as React from "react";
import {
  Grid,
  Modal,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Link,
  Backdrop,
  CircularProgress,
  Box,
  Radio,
  FormControlLabel,
  Switch,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { getHeaders } from "../utils/HeaderHelper";
import { AuthenticationContext, ImagesContext } from "../Context";
import moment from "moment/moment";
import AddAllergy from "./AddAllergy";
import CreateIcon from "@mui/icons-material/Create";
import ArchiveIcon from "@mui/icons-material/Archive";
import { snakeCaseToTitleCase } from "../utils/CaseFormatHelper";
import { useFlash } from "../FlashContext";

interface Props {
  patientId: string;
  allergiesModalOpen: boolean;
  closeAllergiesModal: any;
  refreshAllergies: () => void;
}

const PatientAllergies: React.FC<Props> = ({
  allergiesModalOpen,
  closeAllergiesModal,
  patientId,
  refreshAllergies,
}) => {
  const authenticationSetting = React.useContext(AuthenticationContext);
  const { showToast } = useFlash();
  const images = React.useContext(ImagesContext);
  const [allergies, setAllergies] = React.useState([]);
  const [selectedAllergy, setSelectedAllergy] = React.useState(null);
  const [addAllergyModalOpen, setAddAllergyModalOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [showArchivedAllergies, setShowArchivedAllergies] =
    React.useState(false);

  const fetchPatientAllergies = React.useCallback(() => {
    setLoading(true);
    fetch(
      `/allergies?patient_id=${patientId}&show_archived=${showArchivedAllergies}`,
      {
        method: "GET",
        headers: getHeaders(authenticationSetting.csrfToken),
      }
    )
      .then((result) => result.json())
      .then((result) => {
        if (!result.success) {
          console.log(result.error);
          setLoading(false);
        } else {
          setAllergies(result.resource.allergies);
          setLoading(false);
        }
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  }, [patientId, authenticationSetting.csrfToken, showArchivedAllergies]);

  React.useEffect(() => {
    fetchPatientAllergies();
  }, [fetchPatientAllergies, showArchivedAllergies]);

  const openAddAllergyModal = () => setAddAllergyModalOpen(true);
  const closeAddAllergyModal = () => {
    setSelectedAllergy(null);
    setAddAllergyModalOpen(false);
  };

  const setAllergyForEdit = (allergy) => {
    setSelectedAllergy(allergy);
    openAddAllergyModal();
  };

  const handleArchive = async (allergy) => {
    try {
      const response = await fetch(`/allergies/${allergy.id}/archive`, {
        method: "PATCH",
        headers: getHeaders(authenticationSetting.csrfToken),
      });

      const result = await response.json();

      if (result.success) {
        showToast({
          title: "Allergy Archived",
          text: "Allergy archived successfully",
          type: "success",
        });
        fetchPatientAllergies();
        refreshAllergies();
      } else {
        showToast({
          title: "Error Archiving Allergy",
          text: "Something went wrong",
          type: "error",
        });
      }
    } catch (error) {
      showToast({
        title: "Error Archiving Allergy",
        text: error.message ?? "Failed to archive allergy",
        type: "error",
      });
    } finally {
    }
  };

  return (
    <>
      <Modal
        open={allergiesModalOpen}
        className={"modal-primary"}
        onClose={closeAllergiesModal}
      >
        <div className="paper" style={{ width: "1060px", height: "90vh" }}>
          <div className="paperInner">
            <Grid container>
              <Grid item xs={12}>
                <div
                  className={`main-header`}
                  style={{
                    marginBottom: "0px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>Patient Allergies</span>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      columnGap: "24px",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showArchivedAllergies}
                          onChange={(event) =>
                            setShowArchivedAllergies(event.target.checked)
                          }
                        />
                      }
                      label="View Archived Allergies"
                      sx={{
                        height: "30px",
                        marginRight: "0px",
                      }}
                    />
                    <Link
                      className="action-link row"
                      style={{ backgroundColor: "transparent" }}
                      onClick={openAddAllergyModal}
                    >
                      <img
                        src={images.add_icon}
                        alt="Add Charge"
                        className="action-icon-image"
                        style={{ width: 20, maxHeight: 20 }}
                      />
                      <span className="app-user-text disable-pointer">
                        Add Allergy
                      </span>
                    </Link>
                    <ClearIcon
                      sx={{ width: 30, height: 30, cursor: "pointer" }}
                      onClick={closeAllergiesModal}
                    />
                  </Box>
                </div>
              </Grid>
              <Grid item xs={12}>
                <div className="content" style={{ padding: "0 0 40px 0" }}>
                  <TableContainer sx={{ height: "75vh" }}>
                    <Table stickyHeader>
                      <TableHead
                        sx={{
                          boxShadow: "0px 4px 4px 0px #00000040",
                          backgroundColor: "#fff",
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                        }}
                      >
                        <TableRow
                          sx={{
                            "& td, & th": {
                              borderBottom: "none",
                            },
                          }}
                        >
                          <TableCell
                            align="left"
                            sx={{ paddingLeft: "40px", width: "165px" }}
                          >
                            Allergen
                          </TableCell>
                          <TableCell align="left">Type</TableCell>
                          <TableCell align="left" sx={{ width: "153px" }}>
                            Description of Reaction
                          </TableCell>
                          <TableCell align="left">Severity</TableCell>
                          <TableCell align="left">Date Added</TableCell>
                          {showArchivedAllergies && (
                            <TableCell align="left">Date Removed</TableCell>
                          )}
                          <TableCell align="left">Added by</TableCell>
                          <TableCell
                            align="center"
                            sx={{ paddingRight: "0px", width: "0px" }}
                          >
                            Edit
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ paddingRight: "40px", width: "0px" }}
                          >
                            Archive
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allergies?.length > 0 ? (
                          allergies?.map((allergy) => (
                            <TableRow
                              sx={{ "& td, & th": { borderBottom: "none" } }}
                            >
                              <TableCell
                                align="left"
                                sx={{ paddingLeft: "40px", width: "165px" }}
                              >
                                {allergy.allergen}
                              </TableCell>
                              <TableCell align="left">
                                {snakeCaseToTitleCase(allergy.reaction_type)}
                              </TableCell>
                              <TableCell align="left">
                                {allergy.reaction}
                              </TableCell>
                              <TableCell align="left">
                                {snakeCaseToTitleCase(allergy.severity)}
                              </TableCell>
                              <TableCell align="left">
                                {moment(allergy.date_added).format(
                                  "MM/DD/YYYY"
                                )}
                              </TableCell>
                              {showArchivedAllergies && (
                                <TableCell align="left">
                                  {allergy.date_removed
                                    ? moment(allergy.date_removed).format(
                                        "MM/DD/YYYY"
                                      )
                                    : "N/A"}
                                </TableCell>
                              )}
                              <TableCell align="left">
                                {allergy.added_by_provider?.name}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{ paddingRight: "0px" }}
                              >
                                <Link
                                  style={{
                                    color: "black",
                                  }}
                                  onClick={() => setAllergyForEdit(allergy)}
                                >
                                  <CreateIcon />
                                </Link>
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{ paddingRight: "40px" }}
                              >
                                {!allergy?.date_removed && (
                                  <Link
                                    style={{
                                      color: "black",
                                    }}
                                    onClick={() => handleArchive(allergy)}
                                  >
                                    <ArchiveIcon />
                                  </Link>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow
                            sx={{ "& td, & th": { borderBottom: "none" } }}
                          >
                            <TableCell align="center" colSpan={7}>
                              <Typography
                                style={{
                                  fontWeight: 700,
                                  fontSize: "12px",
                                  lineHeight: "22px",
                                  color: "black",
                                }}
                              >
                                This patient does not have any allergies yet.
                                You can create their first allergy&nbsp;
                                <Link
                                  sx={{
                                    color: "#2B6CB0",
                                    textDecoration: "underline",
                                  }}
                                  onClick={openAddAllergyModal}
                                >
                                  here!
                                </Link>
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              </Grid>
            </Grid>
          </div>
        </div>
      </Modal>

      {addAllergyModalOpen && (
        <AddAllergy
          addAllergyModalOpen={addAllergyModalOpen}
          closeAddAllergyModal={closeAddAllergyModal}
          patientId={patientId}
          selectedAllergy={selectedAllergy}
          onSuccess={() => {
            fetchPatientAllergies();
            refreshAllergies();
          }}
        />
      )}

      <Backdrop open={loading} style={{ zIndex: 9999, color: "#fff" }}>
        <CircularProgress />
      </Backdrop>
    </>
  );
};

export default PatientAllergies;
